import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { StreamLevel } from '../realtime/orderQueue';

/**
 * 离线状态条（`05-ui-design-system.md` §6.8 的「刚离线」行、§6.8 冻结 testid 表的 `offline-status-bar`）。
 *
 * **与顶栏 `connection-badge` 是两个元素，别合并**（§5.6 的 Phase 9 冻结条款逐字要求分开断言）：
 *   · 状态条 = 离线时出现在顶部的**整宽黑条**，文案 `离线 · 已缓存 N 个地点 · N 笔待上传`；
 *   · 徽标 = 顶栏右侧那个小蓝标，文案 `实时` / `轮询模式` / `纯扫码`（testid `connection-badge`，**不改名**）。
 * 把两者混成一条断言，会让「只实现了一个」全绿。
 *
 * 触发条件（§6.8）：`window` 的 `offline` / `online` 事件，或**三级降级**（`level === 'manual'`，
 * 即完全离线）。二级降级（`轮询模式`）**不**出这条：那时网络是通的，说「离线」是假话。
 *
 * ⚠️ `pendingCount` 在本任务恒为 0，而 0 在这里是**真值**而不是占位：离线队列（IndexedDB 写入与
 * 待上传计数）是 Task 9 的交付物，此刻没有任何写操作会入队。
 */
export interface OfflineStatusBarProps {
  /** 三级降级状态机的当前级别（`useOrderStream` 的 `level`） */
  level: StreamLevel;
  /** 已缓存的地点个数（`GET /locations` 手里那份列表的长度） */
  cachedLocations: number;
  /** 待上传的记录数（离线队列；Task 9 接线前恒为 0） */
  pendingCount: number;
}

export default function OfflineStatusBar({ level, cachedLocations, pendingCount }: OfflineStatusBarProps) {
  const [browserOnline, setBrowserOnline] = useState<boolean>(() => navigator.onLine !== false);

  useEffect(() => {
    const goOnline = (): void => setBrowserOnline(true);
    const goOffline = (): void => setBrowserOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  // 在线（一级/二级）时**不占屏**：§6.8 的第一行就是「在线 | 无任何提示」
  if (browserOnline && level !== 'manual') return null;

  return (
    <div
      data-testid="offline-status-bar"
      role="status"
      aria-live="polite"
      className="flex items-center gap-2 border-b-2 border-black bg-black px-3 py-1 text-sm font-bold text-white"
    >
      <span className="h-2 w-2 rounded-full bg-white" />
      <span>
        离线 · 已缓存 {cachedLocations} 个地点 · {pendingCount} 笔待上传
      </span>
      {/*
       * §6.8 冻结的「查看」入口，指向离线队列页。
       * ⚠️ 该页（`/cashier/offline`）是 Task 9 的交付物：本任务只放锚点，不放页面。
       */}
      <Link to="/cashier/offline" className="underline">
        查看
      </Link>
    </div>
  );
}
